import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ShieldCheck, Filter, UserCog, Trash2, Pencil, Plus, Clock, Mail, Database,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIONS = [
  { value: "", label: "Alle handlinger" },
  { value: "create", label: "Opprettet" },
  { value: "update", label: "Endret" },
  { value: "delete", label: "Slettet" },
  { value: "role_change", label: "Rolle-endring" },
];

const ENTITIES = [
  { value: "", label: "Alle moduler" },
  { value: "booking", label: "Booking" },
  { value: "user", label: "Bruker" },
  { value: "payment", label: "Betaling" },
  { value: "receipt", label: "Kvittering" },
  { value: "volt_training", label: "Volt-trening" },
  { value: "boat", label: "Båt" },
  { value: "defect", label: "Avvik" },
  { value: "incident", label: "Skadesak" },
  { value: "inventory", label: "Inventar" },
  { value: "boat_lift", label: "Båtløft" },
  { value: "product", label: "Produkt" },
  { value: "order", label: "Ordre" },
  { value: "survey", label: "Spørring" },
  { value: "booking_note", label: "Interne notater" },
];

function ActionBadge({ action }) {
  const map = {
    create: { icon: Plus, cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Opprettet" },
    update: { icon: Pencil, cls: "bg-sky-100 text-sky-700 border-sky-200", label: "Endret" },
    delete: { icon: Trash2, cls: "bg-red-100 text-red-700 border-red-200", label: "Slettet" },
    role_change: { icon: UserCog, cls: "bg-amber-100 text-amber-700 border-amber-200", label: "Rolle-endring" },
  };
  const m = map[action] || { icon: Database, cls: "bg-gray-100 text-gray-700 border-gray-200", label: action };
  const Icon = m.icon;
  return (
    <Badge className={`${m.cls} border text-[10px] uppercase tracking-widest`}>
      <Icon className="h-3 w-3 mr-1" /> {m.label}
    </Badge>
  );
}

export default function AuditLogPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (entity) params.entity = entity;
      if (action) params.action = action;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/admin/audit-log", { params });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const low = q.toLowerCase();
    return rows.filter((r) =>
      (r.actor_name || "").toLowerCase().includes(low)
      || (r.actor_email || "").toLowerCase().includes(low)
      || (r.label || "").toLowerCase().includes(low)
      || (r.entity_id || "").toLowerCase().includes(low),
    );
  }, [rows, q]);

  const stats = useMemo(() => {
    const byAction = { create: 0, update: 0, delete: 0, role_change: 0, other: 0 };
    rows.forEach((r) => { byAction[r.action] !== undefined ? byAction[r.action]++ : byAction.other++; });
    return byAction;
  }, [rows]);

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto p-4 lg:p-8 space-y-5">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#0F1A2B] flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-[#A57F39]" /> Revisjonslogg
          </h1>
          <p className="text-sm text-[#2B3F4D]/70 mt-1 max-w-3xl">
            Full historikk over alle endringer gjort av admin-brukere. Kun hovedadmin ser denne siden.
          </p>
          <div className="flex flex-wrap gap-4 mt-3 text-xs">
            <span className="flex items-center gap-1 text-emerald-700"><Plus className="h-3 w-3" /> {stats.create} opprettet</span>
            <span className="flex items-center gap-1 text-sky-700"><Pencil className="h-3 w-3" /> {stats.update} endret</span>
            <span className="flex items-center gap-1 text-red-700"><Trash2 className="h-3 w-3" /> {stats.delete} slettet</span>
            {stats.role_change > 0 && (
              <span className="flex items-center gap-1 text-amber-700"><UserCog className="h-3 w-3" /> {stats.role_change} rolle-endringer</span>
            )}
          </div>
        </div>

        <Card className="border-[#A57F39]/20">
          <CardContent className="p-4 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[220px]">
              <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 block flex items-center gap-1">
                <Filter className="h-3 w-3" /> Søk (navn, e-post, hva)
              </label>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="F.eks. Ernst, slettet booking, payments…" data-testid="audit-search" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 block">Modul</label>
              <select value={entity} onChange={(e) => setEntity(e.target.value)} data-testid="audit-entity"
                className="rounded-md border border-[#A57F39]/25 px-3 py-2 text-sm text-[#0F1A2B]">
                {ENTITIES.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 block">Handling</label>
              <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="audit-action"
                className="rounded-md border border-[#A57F39]/25 px-3 py-2 text-sm text-[#0F1A2B]">
                {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 block">Fra</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#0F1A2B]/60 mb-1 block">Til</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <Button onClick={load} className="bg-[#0F1A2B] hover:bg-[#2B3F4D] text-white" data-testid="audit-apply">
              Oppdater
            </Button>
          </CardContent>
        </Card>

        <Card className="border-[#A57F39]/20">
          <CardContent className="p-0 divide-y divide-[#A57F39]/10">
            {loading && <div className="p-6 text-sm text-[#2B3F4D]/60">Laster…</div>}
            {!loading && filtered.length === 0 && (
              <div className="p-10 text-center text-sm text-[#2B3F4D]/60">Ingen hendelser matcher filteret.</div>
            )}
            {filtered.map((r) => (
              <div key={r.id} className="p-3 lg:p-4 flex flex-wrap items-start gap-3 hover:bg-[#F8F7F2]/50">
                <div className="flex items-center gap-2 shrink-0 min-w-[150px]">
                  <ActionBadge action={r.action} />
                  <span className="text-[11px] uppercase tracking-widest text-[#A57F39]">{r.entity}</span>
                </div>
                <div className="flex-1 min-w-[280px]">
                  <div className="text-sm text-[#0F1A2B] font-medium">{r.label || "—"}</div>
                  {r.meta?.changes && Object.keys(r.meta.changes).length > 0 && (
                    <ul className="mt-1 text-xs text-[#2B3F4D]/80 space-y-0.5">
                      {Object.entries(r.meta.changes).map(([k, v]) => (
                        <li key={k}>
                          <strong>{k}:</strong>{" "}
                          <span className="text-red-600 line-through">{String(v.from ?? "")}</span>{" → "}
                          <span className="text-emerald-700">{String(v.to ?? "")}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {r.meta?.path && !r.meta?.changes && (
                    <div className="text-[11px] font-mono text-[#2B3F4D]/60 mt-0.5">
                      {r.meta.method} {r.meta.path}
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-[#2B3F4D]/70 space-y-0.5 min-w-[180px]">
                  <div className="flex items-center gap-1"><UserCog className="h-3 w-3" />{r.actor_name || "–"}{r.is_super_admin && <span className="text-[#A57F39]">★</span>}</div>
                  {r.actor_email && <div className="flex items-center gap-1"><Mail className="h-3 w-3" />{r.actor_email}</div>}
                  <div className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(r.created_at).toLocaleString("no-NO")}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
